import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DownOutlined, LoadingOutlined, UpOutlined } from '@ant-design/icons';
import {
  FONT_SIZE, FONT_WEIGHT, SPACING, RADIUS, SHADOW, LINE_HEIGHT, isDrop,
} from '../../constants/constants';
import { DROP_SEVERITY_LABEL, DropSeverity } from '../../types/card';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useStorePriceName } from '../../hooks/UseStorePriceName';
import { useIsPhone } from '../../hooks/UseIsCompact';
import { useCardPage } from '../../contexts/CardPageContext';
import { useCardEdit } from '../../contexts/CardEditContext';
import { useStorage } from '../../contexts/StorageContext';
import { READ_ONLY_REASON } from '../../constants/readOnly';
import { AppButton } from '../common/AppButton';
import type { DropTableTab } from './DropReviewTable';

// ─── BucketCard ───────────────────────────────────────────────────────────────

const BucketCard: React.FC<{
  title:    string;
  tone:     { text: string; bg: string; border: string };
  count:    number;
  body:     React.ReactNode;
  action?:  React.ReactNode;
  /** 시세 대조가 아직 도는 중이고 이 칸이 0건 — "0건" 대신 "대조 중" 을 보인다(결과 없음과 구분). */
  pending?: boolean;
}> = ({ title, tone, count, body, action, pending }) => {
  const t = useThemeTokens();
  return (
    <div style={{
      border: `1px solid ${tone.border}`, background: tone.bg, borderRadius: RADIUS.lg,
      padding: `${SPACING.md}px`, display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold, color: tone.text }}>
        {title}
      </div>
      <div style={{ fontSize: FONT_SIZE.xl, fontWeight: FONT_WEIGHT.bold, color: tone.text, lineHeight: 1.1, marginTop: SPACING.xs }}>
        {pending ? (
          <span style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.semibold }}>대조 중…</span>
        ) : (
          <>
            {count.toLocaleString()}
            <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, marginLeft: 3 }}>건</span>
          </>
        )}
      </div>
      <div style={{
        color: t.textSecondary, fontSize: FONT_SIZE.xs, marginTop: SPACING.xs,
        lineHeight: LINE_HEIGHT.relaxed, flex: 1,
      }}>
        {body}
      </div>
      {action && <div style={{ marginTop: SPACING.sm }}>{action}</div>}
    </div>
  );
};

// ─── 접힘 상태(감사 card-controltower#326) ──────────────────────────────────────

/** 분류 카드 접힘 저장 키 — 사용자별(같은 브라우저의 다른 계정이 서로 덮지 않게). 화면 배치 취향이라 매장과는 무관하다. */
export const DROP_PANEL_COLLAPSED_KEY = 'cct.dropPanel.collapsed';
export function dropPanelCollapsedKey(username: string | null | undefined): string {
  return `${DROP_PANEL_COLLAPSED_KEY}${username ? `:user:${username}` : ''}`;
}
function readCollapsed(key: string | undefined): boolean {
  if (!key) return false;
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}

// ─── DropClassificationPanel ──────────────────────────────────────────────────

/**
 * "하락 N건을 타 샵 시세와 대조해 분류했습니다" — DailyWorkflowPage 전용(하락 검토 탭).
 * 하락 카드를 dropBucket으로 3분류해 보여주고, 시세 범위 내는 일괄 적용, 확인 필요는
 * 재설계된 모달로 검토하게 한다("하락 검토 개선안", 2026-08-27). WorkflowStatusBar를 대체.
 */
export const DropClassificationPanel: React.FC<{
  /** 카드 안 "목록 보기" → 하단 표의 탭 전환. */
  onShowTab?: (tab: DropTableTab) => void;
  /**
   * 접힘 상태를 남길 localStorage 키(감사 card-controltower#326) — 주면 접기 · 펼치기 버튼이 생긴다. 기본은 펼침.
   * 화면이 사용자별 키(dropPanelCollapsedKey)를 만들어 넘긴다.
   */
  collapseKey?: string;
  /** "하락 N건" → 표의 '하락 검토' 칩(저장 필터 칩과 같은 조건 — 가격 목록 개편 PR 3, 감사 card-controltower#697). */
  onShowDrops?: () => void;
}> = ({ onShowTab, collapseKey, onShowDrops }) => {
  const t = useThemeTokens();
  const sp = useStorePriceName();
  const phone = useIsPhone();
  const { cards, jobs, setReviewOpen } = useCardPage();
  const edit = useCardEdit();
  // 조회 전용(VIEWER) — 일괄 적용 · 되돌리기는 잠그고(이유 표시), 검토 창은 '보기' 로(유저 플로우 제안 7).
  const { isAdmin } = useStorage();
  const readOnly = !isAdmin;
  const [collapsed, setCollapsedState] = useState<boolean>(() => readCollapsed(collapseKey));
  useEffect(() => { setCollapsedState(readCollapsed(collapseKey)); }, [collapseKey]);
  const setCollapsed = useCallback((on: boolean) => {
    setCollapsedState(on);
    if (!collapseKey) return;
    try { localStorage.setItem(collapseKey, on ? '1' : '0'); } catch { /* 저장 못 해도 이번 화면에서는 바뀐다 */ }
  }, [collapseKey]);
  // 접기 · 펼치기는 버튼 자리가 바뀌어(펼친 머리줄 ↔ 접힌 한 줄) 버튼이 새로 그려진다 — 누른 뒤 새 버튼으로 포커스를 옮겨
  // 키보드 사용자가 자리를 잃지 않게 한다(코덱 셀프 리뷰 낮음, 규칙 13).
  const toggleWrapRef = useRef<HTMLSpanElement>(null);
  const refocusToggle = useRef(false);
  useEffect(() => {
    if (!refocusToggle.current) return;
    refocusToggle.current = false;
    toggleWrapRef.current?.querySelector('button')?.focus();
  }, [collapsed]);

  const groups = useMemo(() => {
    const drops = cards.cards.filter((c) => isDrop(c.priceChangeStatus));
    const inRange = drops.filter((c) => c.dropBucket === 'IN_RANGE');
    const suspect = drops.filter((c) => c.dropBucket === 'SUSPECT');
    const noRef   = drops.filter((c) => c.dropBucket === 'NO_REFERENCE');
    const unclassified = drops.filter((c) => c.dropBucket == null);
    const riseCount = cards.cards.filter((c) => c.priceChangeStatus === 'INCREASED').length;

    const severity: Record<DropSeverity, number> = { SEVERE: 0, MODERATE: 0, MINOR: 0 };
    suspect.forEach((c) => { if (c.dropSeverity) severity[c.dropSeverity] += 1; });

    return { drops, inRange, suspect, noRef, unclassified, severity, riseCount };
  }, [cards.cards]);

  const { drops, inRange, suspect, noRef, unclassified, severity, riseCount } = groups;

  if (drops.length === 0) return null;

  const inRangePendingIds = inRange.filter((c) => edit.pendingConfirmIds.has(c.id)).map((c) => c.id);
  const inRangeAppliedCount = inRange.length - inRangePendingIds.length;

  // 대조 중 표시는 분류 잡이 실제로 도는 동안만 — 잡이 실패 · 시작 실패로 끝나 미분류가 남으면 "대조하는 중" 이 계속
  // 떠 있었다(코덱 셀프 리뷰 PR #50). 그때는 미분류 건수만 따로 알린다.
  const classifyingNow = jobs.classifying;
  const leftUnclassified = !classifyingNow && unclassified.length > 0;

  const tap: React.CSSProperties | undefined = phone ? { height: 44 } : undefined;
  // 요약 건수 → 같은 조건의 칩 목록(개편 §2-3 '대시보드 연결').
  const dropsLink = onShowDrops && drops.length > 0 ? (
    <AppButton variant="link" size="sm" style={phone ? { height: 44 } : { height: 'auto', padding: 0 }}
      data-testid="drop-classification-show-drops" onClick={onShowDrops}>
      하락 검토 {drops.length.toLocaleString()}건 목록
    </AppButton>
  ) : null;
  const toggle = collapseKey ? (
    <span ref={toggleWrapRef} style={{ marginLeft: 'auto', display: 'inline-flex' }}>
      <AppButton
        variant="link" size="sm"
        style={{ height: phone ? 44 : 'auto', padding: phone ? '0 8px' : 0 }}
        aria-expanded={!collapsed}
        aria-controls={collapsed ? undefined : 'drop-classification-cards'}
        icon={collapsed ? <DownOutlined /> : <UpOutlined />}
        onClick={() => { refocusToggle.current = true; setCollapsed(!collapsed); }}
      >
        {collapsed ? '요약 펼치기' : '요약 접기'}
      </AppButton>
    </span>
  ) : null;

  // 접힘(감사 card-controltower#326) — 세 칸을 한 줄 요약과 주요 버튼(일괄 적용 · 검토하기)으로 줄여 아래 목록에 자리를 준다.
  if (collapseKey && collapsed) {
    const count = (n: number, color: string, label: string, pending: boolean) => (
      <span style={{ fontSize: FONT_SIZE.sm, color: t.textSecondary, whiteSpace: 'nowrap' }}>
        {label}{' '}
        <b style={{ color, fontWeight: FONT_WEIGHT.bold }}>{pending ? '대조 중' : `${n.toLocaleString()}건`}</b>
      </span>
    );
    return (
      <div
        data-testid="drop-classification-collapsed"
        style={{
          display: 'flex', alignItems: 'center', gap: `${SPACING.xxs}px ${SPACING.sm}px`, flexWrap: 'wrap',
          background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: RADIUS.lg,
          boxShadow: SHADOW.card, padding: `${SPACING.xs}px ${SPACING.md}px`,
        }}
      >
        <span style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary, whiteSpace: 'nowrap' }}>
          하락 {drops.length.toLocaleString()}건
        </span>
        {dropsLink}
        {count(inRange.length, t.success, '시세 범위 내', classifyingNow && inRange.length === 0)}
        {count(suspect.length, t.danger, '확인 필요', classifyingNow && suspect.length === 0)}
        {count(noRef.length, t.warning, '참조 없음', classifyingNow && noRef.length === 0)}
        {classifyingNow && (
          <span style={{ fontSize: FONT_SIZE.xs, color: t.warning, whiteSpace: 'nowrap' }}>
            <LoadingOutlined /> 시세 대조 중 {(drops.length - unclassified.length).toLocaleString()} / {drops.length.toLocaleString()}
          </span>
        )}
        {leftUnclassified && (
          <span style={{ fontSize: FONT_SIZE.xs, color: t.warning }}>
            미분류 {unclassified.length.toLocaleString()}건 — 펼쳐서 확인
          </span>
        )}
        {inRangePendingIds.length > 0 ? (
          <AppButton
            variant="primary" size="sm"
            style={readOnly ? tap : { ...tap, background: t.successSolid, borderColor: t.successSolid }}
            disabled={readOnly} disabledReason={readOnly ? READ_ONLY_REASON : undefined}
            onClick={() => { if (!readOnly) edit.applyDropsBulk(inRange.filter((c) => edit.pendingConfirmIds.has(c.id))); }}
          >
            {inRangePendingIds.length.toLocaleString()}건 일괄 적용
          </AppButton>
        ) : inRange.length > 0 ? (
          <span style={{ fontSize: FONT_SIZE.xs, color: t.success, fontWeight: FONT_WEIGHT.semibold, whiteSpace: 'nowrap' }}>
            ✓ {inRangeAppliedCount.toLocaleString()}건 적용됨
          </span>
        ) : null}
        {suspect.length > 0 && (
          <AppButton variant="primary" size="sm" style={tap} onClick={() => { onShowTab?.('suspect'); setReviewOpen(true); }}>
            {suspect.length.toLocaleString()}건 {readOnly ? '보기' : '검토하기'}
          </AppButton>
        )}
        {toggle}
      </div>
    );
  }

  return (
    <div style={{
      background: t.bgSurface, border: `1px solid ${t.border}`, borderRadius: RADIUS.lg,
      boxShadow: SHADOW.card, padding: `${SPACING.md}px ${SPACING.lg}px`,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACING.sm, flexWrap: 'wrap' }}>
        {/* 대조가 도는 중에 "분류했습니다" 와 0건이 먼저 보였다(2026-10-21 코덱 화면 점검, 감사 card-controltower#64). */}
        <span style={{ fontSize: FONT_SIZE.md, fontWeight: FONT_WEIGHT.semibold, color: t.textPrimary }}>
          {classifyingNow
            ? `하락 ${drops.length.toLocaleString()}건을 타 샵 시세와 대조하는 중입니다`
            : `하락 ${drops.length.toLocaleString()}건을 타 샵 시세와 대조해 분류했습니다`}
        </span>
        {dropsLink}
        {riseCount > 0 && (
          <span style={{ fontSize: FONT_SIZE.xs, color: t.textSecondary }}>
            상승 {riseCount.toLocaleString()}건은 확인 없이 반영됩니다
          </span>
        )}
        {classifyingNow && (
          <span style={{ fontSize: FONT_SIZE.xs, color: t.warning }}>
            <LoadingOutlined /> 시세 대조 중 {(drops.length - unclassified.length).toLocaleString()} / {drops.length.toLocaleString()}
            {unclassified.length > 0 && ` · 미분류 ${unclassified.length.toLocaleString()}건`}
          </span>
        )}
        {leftUnclassified && (
          <span style={{ fontSize: FONT_SIZE.xs, color: t.warning }}>
            미분류 {unclassified.length.toLocaleString()}건 — 시세 대조가 끝나지 않았습니다(잠시 뒤 새로고침해 보고, 그래도 남으면 ① 가격 받기)
          </span>
        )}
        {toggle}
      </div>

      {/* 390 에서 3열 고정이면 칸이 약 97px 라 설명이 한 글자씩 꺾였다(감사 card-controltower#64) — 칸이 220px 보다
          좁아지면 줄을 바꾼다(휴대폰 한 열, 넓은 폭 세 열). */}
      <div id="drop-classification-cards" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: SPACING.sm, marginTop: SPACING.md,
      }}>
        {/* 시세 범위 내 */}
        <BucketCard
          title="시세 범위 내 (안전)"
          tone={{ text: t.success, bg: t.tintSuccess, border: t.lineSuccess }}
          count={inRange.length}
          pending={classifyingNow && inRange.length === 0}
          body={<>{sp}이 다른 샵 판매가 <b>최저~최고 사이</b>에 있습니다. 개별 확인 없이 일괄 적용해도 됩니다.</>}
          action={
            inRange.length === 0 ? null : (
              <>
                {inRangePendingIds.length > 0 ? (
                  <AppButton
                    variant="primary" size="sm"
                    style={readOnly ? { ...tap, width: '100%' } : { ...tap, width: '100%', background: t.successSolid, borderColor: t.successSolid }}
                    disabled={readOnly} disabledReason={readOnly ? READ_ONLY_REASON : undefined}
                    onClick={() => { if (!readOnly) edit.applyDropsBulk(inRange.filter((c) => edit.pendingConfirmIds.has(c.id))); }}
                  >
                    {inRangePendingIds.length.toLocaleString()}건 일괄 적용
                  </AppButton>
                ) : (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: SPACING.xs,
                    fontSize: FONT_SIZE.sm, color: t.success, fontWeight: FONT_WEIGHT.semibold,
                  }}>
                    <span>✓ {inRangeAppliedCount.toLocaleString()}건 적용됨</span>
                    <AppButton
                      variant="link" size="sm" style={{ marginLeft: 'auto', height: phone ? 44 : 'auto' }}
                      disabled={readOnly} disabledReason={readOnly ? READ_ONLY_REASON : undefined}
                      onClick={() => { if (!readOnly) edit.restorePendingMany(inRange.map((c) => c.id)); }}
                    >
                      되돌리기
                    </AppButton>
                  </div>
                )}
                {onShowTab && (
                  <div style={{ textAlign: 'center', marginTop: SPACING.xxs }}>
                    <AppButton variant="link" size="sm" style={{ height: phone ? 44 : 'auto' }} onClick={() => onShowTab('in_range')}>
                      {inRange.length.toLocaleString()}건 목록 보기
                    </AppButton>
                  </div>
                )}
              </>
            )
          }
        />

        {/* 확인 필요 */}
        <BucketCard
          title="확인 필요 (오등록 의심)"
          tone={{ text: t.danger, bg: t.tintDanger, border: t.lineDanger }}
          count={suspect.length}
          pending={classifyingNow && suspect.length === 0}
          body={
            <>
              {sp}이 <b>타 샵 시세를 벗어났거나</b> 참고 판매처가 부족합니다. 한 건씩 확인해야 합니다.
              {suspect.length > 0 && (
                <div style={{ marginTop: SPACING.xs, display: 'flex', gap: SPACING.xs, flexWrap: 'wrap' }}>
                  {(['SEVERE', 'MODERATE', 'MINOR'] as DropSeverity[])
                    .filter((s) => severity[s] > 0)
                    .map((s) => (
                      <span key={s} style={{
                        fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.semibold, color: t.danger,
                        border: `1px solid ${t.lineDanger}`, borderRadius: RADIUS.sm, padding: '0 6px',
                      }}>
                        {DROP_SEVERITY_LABEL[s]} {severity[s]}
                      </span>
                    ))}
                </div>
              )}
            </>
          }
          action={
            <AppButton
              variant="primary" size="sm" style={{ ...tap, width: '100%' }}
              disabled={suspect.length === 0}
              disabledReason={classifyingNow ? '시세 대조가 끝나면 검토할 수 있습니다' : '확인이 필요한 하락 카드가 없습니다'}
              onClick={() => { onShowTab?.('suspect'); setReviewOpen(true); }}
            >
              {suspect.length > 0 ? `${suspect.length.toLocaleString()}건 ${readOnly ? '보기' : '검토하기'}` : classifyingNow ? '대조 중' : '검토 완료'}
            </AppButton>
          }
        />

        {/* 참조 시세 없음 */}
        <BucketCard
          title="참조 시세 없음"
          tone={{ text: t.warning, bg: t.tintWarning, border: t.lineWarning }}
          count={noRef.length}
          pending={classifyingNow && noRef.length === 0}
          // ③ 스토어 반영이 이 카드들을 대상에서 뺀다(2026-10-21) — 예전 문구("<매장> 가격(옛 추천가)이 그대로 반영")와 표의
          // "반영 제외" 표기가 서로 달랐다. 검증할 수 없는 하락은 내리지 않는 쪽으로 맞췄다.
          body={<>대조할 타 샵 판매가가 없어 검증할 수 없습니다. <b>③ 스토어 반영에서 제외</b>되고 네이버 가격이 유지됩니다.</>}
          action={onShowTab && noRef.length > 0 ? (
            <AppButton variant="default" size="sm" style={{ ...tap, width: '100%' }} onClick={() => onShowTab('no_ref')}>
              목록 보기
            </AppButton>
          ) : undefined}
        />
      </div>

      <div style={{
        marginTop: SPACING.sm, borderTop: `1px solid ${t.borderSubtle}`, paddingTop: SPACING.xs,
        display: 'flex', gap: SPACING.md, flexWrap: 'wrap',
        fontSize: FONT_SIZE.xs, color: t.textMuted,
      }}>
        <span style={{ fontWeight: FONT_WEIGHT.semibold, color: t.textSecondary }}>의심 판정 기준</span>
        <span>{sp} &lt; 타 샵 최저가</span>
        <span>하락률 70% 이상</span>
        <span>참고 판매처 1곳 이하</span>
      </div>
    </div>
  );
};
